import { ManualOpForm } from './ledger/ManualOpForm';
import { REFERRAL_OP_ASSET_TYPE_ID } from '@pokerroom/types';
import { sessionVenueKey, useAuthStore } from '../../store/auth.store';
import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
import { MemberReferrals } from './MemberReferrals';
import { MemberMembershipFee } from './MemberMembershipFee';
import { MemberTags } from './MemberTags';
import { MemberLevelGrants } from './MemberLevelGrants';
import { useMemberDeactivationConfirm } from './MemberDeactivationConfirm';
import type { MemberLevelDto } from '@pokerroom/types';
/**
 * @page MemberDetailPage
 * @description 會員詳情頁（UI-3-2）
 * 頁首、各帳本餘額摘要列，以及每個帳本一個分頁（會員資訊 / 選手點數 / 限時月份 / Z點 / ZIPA積分）
 */

import { useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { useDialogFocus } from '../../hooks/useDialogFocus';
import { api } from '../../lib/api';
import { useEditionStore } from '../../store/edition.store';
import { LedgerSummaryStrip } from './ledger/LedgerSummaryStrip';
import { MemberInfoTab } from './ledger/MemberInfoTab';
import { MemberLedgerTabs } from './ledger/MemberLedgerTabs';
import { buildLedgerTabs, resolveTab, type LedgerTab } from './ledger/ledgerTabs';
import { LevelBadge, StatusBadge } from './memberBadges';
import type { MemberDto, MemberLedgerSummaryDto, DeleteMemberImpactDto } from '@pokerroom/types';

// The API answers 400 for a malformed id and 404 for an unknown one: both mean "no such member".
const isMissing = (error: unknown) =>
  isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 400);

const debtAmount = (totalDebt: string) => {
  const value = parseFloat(totalDebt);
  return Number.isFinite(value) && value > 0 ? value : null;
};

const outlineButton =
  'inline-flex min-h-11 items-center px-4 rounded-md border border-base-300 text-sm text-base-content hover:bg-base-300';

export default function MemberDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('member');
  const { t: tc } = useTranslation('common');
  const qc = useQueryClient();

  const actor = useAuthStore((s) => s.user?.id);
  const revision = useAuthStore((s) => s.sessionRevision);
  const venue = useAuthStore((s) => sessionVenueKey(s.accessToken));
  const memberQueryKey = ['member', id, actor, venue, revision];
  const [searchParams, setSearchParams] = useSearchParams();
  const deactivation = useMemberDeactivationConfirm(id);
  const { data: levels = [] } = useQuery({
    queryKey: ['member-levels'],
    queryFn: () => api.get<MemberLevelDto[]>('/members/levels'),
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const dialogRef = useDialogFocus(showDeleteDialog);

  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  // The More panel closes on a click outside it; Escape is handled on the panel itself.
  // While one of its dialogs is open it stays, so focus can return to the action that opened it.
  const dialogOpen = showDeleteDialog || deactivation.open;
  useEffect(() => {
    if (!moreOpen || dialogOpen) return;
    const close = (event: PointerEvent) => {
      if (!moreRef.current?.contains(event.target as Node)) setMoreOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [moreOpen, dialogOpen]);

  const {
    data: member,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery<MemberDto>({
    queryKey: memberQueryKey,
    queryFn: () => api.get<MemberDto>(`/members/${id!}`),
    enabled: !!id,
  });

  const { data: summary, error: summaryError } = useQuery<MemberLedgerSummaryDto>({
    queryKey: ['member-ledger-summary', id, actor, venue, revision],
    queryFn: () => api.get<MemberLedgerSummaryDto>(`/members/${id!}/ledger-summary`),
    enabled: !!id && !!member && (!member.associationMode || member.status === 'ACTIVE'),
  });

  const { data: deleteImpact } = useQuery<DeleteMemberImpactDto>({
    queryKey: ['member-delete-impact', id],
    queryFn: () => api.get<DeleteMemberImpactDto>(`/members/${id!}/delete-impact`),
    enabled: showDeleteDialog && !!id,
  });

  const deactivateMutation = useMutation({
    mutationFn: () => api.delete<void>(`/members/${id!}`),
    onSuccess: () => {
      // DELETE returns 204; publish the confirmed status before refetching detail.
      if (member?.associationMode)
        qc.setQueryData<MemberDto>(memberQueryKey, (current) =>
          current ? { ...current, status: 'INACTIVE' } : current,
        );
      qc.invalidateQueries({ queryKey: ['member', id] });
    },
    onError: (err) => {
      console.warn('[MemberDetail] deactivate failed:', (err as Error).message);
    },
  });

  const hardDeleteMutation = useMutation({
    mutationFn: () => api.delete(`/members/${id!}/permanent`),
    onSuccess: () => navigate('/members'),
    onError: (err) => {
      console.warn('[MemberDetail] permanent delete failed:', (err as Error).message);
    },
  });

  // Phase 2A tabs follow the points-config feature gate (member.points).
  const wallet = useEditionStore((s) => s.hasFeature('member.points'));
  const tabs = buildLedgerTabs(summary, wallet);
  const requestedTab = searchParams.get('tab');
  if (
    requestedTab === `asset:${REFERRAL_OP_ASSET_TYPE_ID}` &&
    !tabs.some((tab) => tab.key === requestedTab)
  )
    tabs.push({ key: requestedTab, kind: 'ASSET', assetTypeId: REFERRAL_OP_ASSET_TYPE_ID });
  const activeTab = resolveTab(requestedTab, tabs);
  const selectTab = (tab: LedgerTab) =>
    setSearchParams(tab.kind === 'info' ? {} : { tab: tab.key }, { replace: true });

  const backLink = (
    <Link
      to="/members"
      className="inline-flex min-h-11 items-center text-sm text-base-content/70 hover:text-primary"
    >
      &larr; {t('backToList')}
    </Link>
  );

  if (
    [error, summaryError].some(
      (failure) => isAxiosError(failure) && [401, 403].includes(failure.response?.status ?? 0),
    )
  )
    return (
      <AdminPage header={<AdminPageHeader title={t('detail.title')} back={backLink} />}>
        <p role="alert">{t('detail.loadError')}</p>
      </AdminPage>
    );

  if (isLoading)
    return (
      <AdminPage header={<AdminPageHeader title={t('detail.title')} back={backLink} />}>
        <p role="status" className="flex items-center gap-2 p-6 text-sm text-base-content/70">
          <span
            aria-hidden="true"
            className="h-4 w-4 animate-spin rounded-full border-[1.5px] border-primary border-t-transparent [animation-duration:720ms]"
          />
          {tc('loading')}
        </p>
      </AdminPage>
    );
  // A failed background refetch keeps the loaded page (and any pending form) in place.
  if (isError && !member && !isMissing(error))
    return (
      <AdminPage header={<AdminPageHeader title={t('detail.title')} back={backLink} />}>
        <div role="alert" className="space-y-3">
          <p className="text-base text-base-content">{t('detail.loadError')}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className={outlineButton}
          >
            {t('detail.retry')}
          </button>
        </div>
      </AdminPage>
    );
  if (!member)
    return (
      <AdminPage header={<AdminPageHeader title={t('detail.title')} back={backLink} />}>
        <p role="alert" className="text-base text-base-content">
          {t('memberNotFound')}
        </p>
      </AdminPage>
    );

  const debt = debtAmount(member.totalDebt);
  // Association members who are not active see only their profile, without ledgers.
  const locked = member.associationMode && member.status !== 'ACTIVE';
  const profileExtras = member.associationMode && (
    <>
      <MemberTags memberId={member.id} memberNo={member.memberNo} />
      {member.associationMode && (
        <MemberReferrals
          memberId={member.id}
          memberNo={member.memberNo}
          nickname={member.nickname}
        />
      )}
      <MemberLevelGrants memberId={member.id} levels={levels} />
    </>
  );

  return (
    <AdminPage className="admin-page-body">
      {/* Header */}

      <AdminPageHeader
        title={member.nickname}
        back={backLink}
        description={
          <div className="flex items-center gap-4 min-w-0">
            {member.photoUrl ? (
              <img
                src={member.photoUrl}
                alt=""
                className="h-16 w-16 shrink-0 rounded-full object-cover"
              />
            ) : (
              <div
                aria-hidden="true"
                className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-base-300 text-2xl font-bold text-base-content/70"
              >
                {member.nickname.slice(0, 1)}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                {member.level && <LevelBadge level={member.level} />}
                <StatusBadge status={member.status} />
              </div>
              <p className="text-sm text-base-content/70 tabular-nums">
                <span className="font-mono">{member.memberNo}</span>
                {member.phone && <span className="ml-3">{member.phone}</span>}
              </p>
            </div>
          </div>
        }
        actions={
          <>
            <div className="flex flex-wrap items-start gap-2 md:shrink-0 md:justify-end">
              <button
                type="button"
                onClick={() =>
                  window.open(`/api/members/${id}/qrcode`, '_blank', 'noopener,noreferrer')
                }
                className={outlineButton}
              >
                QR Code
              </button>
              <Link
                to={`/members/${id}/edit`}
                className="inline-flex min-h-11 items-center px-4 rounded-md bg-primary text-primary-content text-sm hover:bg-primary/90"
              >
                {t('editMember')}
              </Link>
              <div
                ref={moreRef}
                className="member-secondary-actions relative"
                onKeyDown={(event) => {
                  if (event.key !== 'Escape' || !moreOpen || dialogOpen) return;
                  setMoreOpen(false);
                  moreRef.current?.querySelector<HTMLElement>('[aria-controls]')?.focus();
                }}
              >
                <button
                  type="button"
                  aria-expanded={moreOpen}
                  aria-controls="member-more-actions"
                  onClick={() => setMoreOpen(!moreOpen)}
                  className={`${outlineButton} gap-2`}
                >
                  {t('detail.more')}
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    className={`h-4 w-4 ${moreOpen ? 'rotate-180' : ''}`}
                  >
                    <path d="M5.5 8l4.5 4.5L14.5 8" strokeLinecap="square" />
                  </svg>
                </button>
                {moreOpen && (
                  <div
                    id="member-more-actions"
                    role="group"
                    aria-label={t('detail.moreActions')}
                    className="absolute right-0 top-full z-20 mt-1 flex min-w-44 flex-col border border-base-300 bg-base-100 p-1"
                  >
                    {member.status !== 'INACTIVE' && (
                      <button
                        type="button"
                        disabled={deactivation.open || deactivateMutation.isPending}
                        onClick={async () => {
                          if (
                            member.associationMode
                              ? await deactivation.ask()
                              : confirm(t('confirmDeactivate'))
                          )
                            deactivateMutation.mutate();
                        }}
                        className="min-h-11 px-3 text-left text-sm text-warning hover:bg-base-300 disabled:opacity-40"
                      >
                        {t('deactivate')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setShowDeleteDialog(true);
                        setDeleteConfirmName('');
                      }}
                      className="min-h-11 px-3 text-left text-sm text-error hover:bg-base-300"
                    >
                      {t('permanentDelete')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        }
      />

      <MemberMembershipFee memberId={member.id} />

      {debt !== null && (
        <p className="mb-4 border border-error/30 bg-error/10 p-3 text-sm text-error">
          {t('debtWarning', { amount: `NT$${debt.toLocaleString()}` })}
        </p>
      )}

      {summary && (!member.associationMode || member.status === 'ACTIVE') && (
        <LedgerSummaryStrip
          summary={summary}
          wallet={wallet}
          activeKey={activeTab.key}
          onSelect={selectTab}
        />
      )}

      {summary?.z.pendingAfterLeaving &&
        (!member.associationMode || member.status === 'ACTIVE') && (
          <div
            role="alert"
            className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-warning/30 bg-warning/10 p-3 text-sm text-warning"
          >
            <span>{t('zPointsPendingNotice', { balance: summary.z.balance })}</span>
            <button
              type="button"
              className="min-h-11 rounded-md border border-warning/40 px-3"
              onClick={() => selectTab({ key: 'z', kind: 'Z' })}
            >
              {t('ledgerTabZ')}
            </button>
          </div>
        )}

      {locked ? (
        <>
          <p role="alert" className="alert alert-warning mb-4">
            {t('memberBalancesLocked')}
          </p>
          <MemberInfoTab member={member} />
          {activeTab.assetTypeId === REFERRAL_OP_ASSET_TYPE_ID && (
            <ManualOpForm memberId={member.id} memberLocked />
          )}
        </>
      ) : (
        <MemberLedgerTabs
          memberId={member.id}
          tabs={tabs}
          active={activeTab}
          summary={summary}
          summaryUnavailable={!!summaryError || !summary}
          onSelect={selectTab}
          info={<MemberInfoTab member={member} />}
          locked={member.status !== 'ACTIVE'}
        />
      )}
      {/* One place for both layouts, so a status change keeps a pending level grant mounted;
          ledger tabs hide it. */}
      {profileExtras && <div hidden={!locked && activeTab.kind !== 'info'}>{profileExtras}</div>}
      {deactivation.dialog}

      {/* 永久刪除確認 Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label={t('permanentDeleteMember')}
            tabIndex={-1}
            className="bg-base-200 rounded-xl shadow-2xl w-full max-w-md max-h-[calc(100dvh-32px)] overflow-y-auto p-4 sm:p-6"
          >
            <h2 className="text-lg font-semibold text-error mb-4">{t('permanentDeleteMember')}</h2>
            <p className="text-sm text-base-content mb-3">
              <Trans t={t} i18nKey="permanentDeleteWarning" components={{ strong: <strong /> }}>
                {{ nickname: member.nickname }}
                {{ memberNo: member.memberNo }}
              </Trans>
            </p>

            {deleteImpact && (
              <div className="mb-4 p-3 bg-error/5 border border-error/20 rounded-lg text-sm space-y-1">
                <p className="font-medium text-error">{t('impactScope')}</p>
                <p className="text-base-content/70">
                  {t('tournamentEntries', { count: deleteImpact.tournamentEntries })}
                </p>
                <p className="text-base-content/70">
                  {t('rewardPointsLogs', { count: deleteImpact.rewardPointsLogs })}
                </p>
                <p className="text-base-content/70">
                  {t('prizeCreditLogs', { count: deleteImpact.prizeCreditLogs })}
                </p>
                <p className="text-base-content/70">
                  {t('customValuesCount', { count: deleteImpact.customValues })}
                </p>
                <p className="text-base-content/70">
                  {t('otherVenueMemberships', { count: deleteImpact.otherVenueMemberships.length })}
                </p>
                {!deleteImpact.canPermanentlyDelete && (
                  <p className="font-medium text-warning">{t('permanentDeleteBlocked')}</p>
                )}
              </div>
            )}

            <label className="block mb-4">
              <span className="text-sm text-base-content/70 mb-1 block">
                <Trans t={t} i18nKey="typeNicknameConfirm" components={{ strong: <strong /> }}>
                  {{ nickname: member.nickname }}
                </Trans>
              </span>
              <input
                type="text"
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-error/30 bg-base-200 text-base min-h-11 focus:outline-hidden focus:ring-2 focus:ring-error"
                placeholder={member.nickname}
              />
            </label>

            {hardDeleteMutation.error && (
              <p className="text-sm text-error mb-3">
                {(hardDeleteMutation.error as Error).message ?? t('deleteFailed')}
              </p>
            )}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteDialog(false)}
                className="min-h-11 px-4 py-2 rounded-md border border-base-300 text-sm"
              >
                {tc('cancel')}
              </button>
              <button
                type="button"
                onClick={() => hardDeleteMutation.mutate()}
                disabled={
                  deleteConfirmName !== member.nickname ||
                  hardDeleteMutation.isPending ||
                  !deleteImpact?.canPermanentlyDelete
                }
                className="min-h-11 px-4 py-2 rounded-md bg-error text-error-content text-sm disabled:opacity-50"
              >
                {hardDeleteMutation.isPending ? tc('loading') : t('confirmPermanentDelete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminPage>
  );
}
